import { useCallback, useLayoutEffect, useRef } from 'react';
import type { ComponentProps, Ref } from 'react';
import { cn } from '../lib/cn.ts';

/**
 * Contextual controls that act on one row, card or block: hidden at rest, shown while the
 * pointer is on the content or focus is inside it. Hidden means opacity and pointer-events,
 * never display, so nothing shifts when they appear.
 *
 * Hover hiding applies only where hover is real (a fine pointer). Under a finger the controls
 * stay visible: every surface that uses this also opens on tap, so the tap is taken
 * (touch="always" in the heuristics' terms).
 *
 *   <div className={REVEAL_GROUP}>... <RevealSlot><IconButton label="Remove worktree x" /></RevealSlot></div>
 *
 * The controls never cover text and never take the place of what is under the pointer. When the
 * slot sits on top of the row (absolute, at the row's end), the row's trailing content goes in a
 * RevealRest. That content (a "Review" button, a time, a chevron) stays where it is, always:
 * the RevealRest reserves the slot's width beside it (the slot publishes `--reveal-w` on its
 * parent), and the slot moves in from the end by the rest's own width (the rest publishes
 * `--reveal-rest-offset` on the group), so the controls appear just before it. A pointer
 * resting on "Review" still clicks Review. A row with no trailing content reserves the space
 * with an empty RevealRest.
 *
 *   <li className={REVEAL_GROUP + ' relative flex'}>
 *     <Pressable className="flex flex-1 pr-4">... <RevealRest>Review</RevealRest></Pressable>
 *     <RevealSlot className="absolute right-4">...</RevealSlot>
 *   </li>
 *
 * The visible variants carry more specificity than the hidden one, so their order in the
 * generated CSS does not matter. A slot whose menu is open (aria-expanded) stays visible.
 */
export const REVEAL_GROUP = 'group/reveal';

export const REVEAL_SLOT = cn(
  'transition-opacity duration-(--duration-fast)',
  'pointer-fine:opacity-0 pointer-fine:pointer-events-none',
  'group-hover/reveal:opacity-100 group-hover/reveal:pointer-events-auto',
  'group-focus-within/reveal:opacity-100 group-focus-within/reveal:pointer-events-auto',
  'has-aria-expanded:opacity-100 has-aria-expanded:pointer-events-auto',
  'forced-colors:opacity-100',
);

/**
 * The trailing content beside the slot: always shown. It used to give way to the controls, which
 * swapped the control under a resting pointer (hover "Review", click something else); kept as
 * an export so callers that style with it keep building.
 */
export const REVEAL_REST = '';

/** The gap between the controls and the trailing content they sit beside. */
const REST_GAP = 4;

type WidthListener = (width: number) => void;
let shared: ResizeObserver | null = null;
const listeners = new Map<Element, WidthListener>();

/**
 * Hear an element's border-box width, rounded like offsetWidth. One observer serves every slot
 * and rest, and the width comes from the observer's entry, after layout: reading offsetWidth in
 * each row's layout effect, between the style writes of the rows before it, forced a layout per
 * row (370 ms opening Projects with 1,200 checkouts, 22 Oct 2026). Without ResizeObserver
 * (server, tests) the width is read once.
 */
export function observeWidth(el: HTMLElement, fn: WidthListener): () => void {
  if (typeof ResizeObserver === 'undefined') {
    fn(el.offsetWidth);
    return () => undefined;
  }
  shared ??= new ResizeObserver((entries) => {
    for (const e of entries) {
      const box = e.borderBoxSize?.[0];
      listeners.get(e.target)?.(Math.round(box ? box.inlineSize : (e.target as HTMLElement).offsetWidth));
    }
  });
  listeners.set(el, fn);
  shared.observe(el, { box: 'border-box' });
  return () => {
    listeners.delete(el);
    shared?.unobserve(el);
  };
}

export function RevealSlot({ className, ref, ...rest }: ComponentProps<'div'>) {
  const own = useRef<HTMLDivElement | null>(null);
  const setRef = useCallback(
    (el: HTMLDivElement | null) => {
      own.current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) (ref as { current: HTMLDivElement | null }).current = el;
    },
    [ref],
  );
  // The slot's width, on its parent, for a RevealRest to hold: the controls then always fit the
  // place they take, whatever they are (one icon, three, an assignee chip).
  useLayoutEffect(() => {
    const el = own.current;
    const host = el?.parentElement;
    if (!el || !host) return;
    const stop = observeWidth(el, (w) => host.style.setProperty('--reveal-w', `${w}px`));
    return () => {
      stop();
      host.style.removeProperty('--reveal-w');
    };
  }, []);
  return (
    <div
      ref={setRef as Ref<HTMLDivElement>}
      data-reveal=""
      className={cn('flex shrink-0 items-center gap-0.5', REVEAL_SLOT, className)}
      {...rest}
      style={{ marginRight: 'var(--reveal-rest-offset, 0px)', ...rest.style }}
    />
  );
}

/**
 * A row's trailing content (a label, a time, a chevron) that stays in place while the
 * RevealSlot's controls show just before it. It reserves the slot's width on its leading side,
 * so the controls never draw on the row's text. Empty, it is the reserved space alone.
 */
export function RevealRest({ className, ref, ...rest }: ComponentProps<'span'>) {
  const own = useRef<HTMLSpanElement | null>(null);
  const setRef = useCallback(
    (el: HTMLSpanElement | null) => {
      own.current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) (ref as { current: HTMLSpanElement | null }).current = el;
    },
    [ref],
  );
  // Its own width, on the group, for the slot to start before it.
  useLayoutEffect(() => {
    const el = own.current;
    const host = el?.closest<HTMLElement>('[class~="group/reveal"]');
    if (!el || !host) return;
    const stop = observeWidth(el, (w) => host.style.setProperty('--reveal-rest-offset', `${w ? w + REST_GAP : 0}px`));
    return () => {
      stop();
      host.style.removeProperty('--reveal-rest-offset');
    };
  }, []);
  return (
    <>
      <span aria-hidden data-reveal-space="" className="w-(--reveal-w) shrink-0" />
      <span ref={setRef as Ref<HTMLSpanElement>} data-reveal-rest="" className={cn('flex shrink-0 items-center justify-end', className)} {...rest} />
    </>
  );
}
